// Shared liveness status presentation. Used by the domain list (dot),
// detail page (badge), and dashboard card so labels/colors stay in sync.

export type LivenessStatusKey =
  | 'live' | 'parked' | 'for_sale' | 'redirect' | 'offline' | 'error';

interface Meta {
  label: string;
  /** Tailwind text + bg + border for a badge. */
  badge: string;
  /** Tailwind bg for a status dot. */
  dot: string;
}

const META: Record<string, Meta> = {
  live:     { label: 'Live',      badge: 'text-green-700 bg-green-50 border-green-200',   dot: 'bg-green-500' },
  parked:   { label: 'Parked',    badge: 'text-amber-700 bg-amber-50 border-amber-200',   dot: 'bg-amber-500' },
  for_sale: { label: 'For sale',  badge: 'text-orange-700 bg-orange-50 border-orange-200', dot: 'bg-orange-500' },
  redirect: { label: 'Redirect',  badge: 'text-blue-700 bg-blue-50 border-blue-200',      dot: 'bg-blue-500' },
  offline:  { label: 'Offline',   badge: 'text-red-700 bg-red-50 border-red-200',         dot: 'bg-red-500' },
  error:    { label: 'Check error', badge: 'text-slate-600 bg-slate-50 border-slate-200', dot: 'bg-slate-400' },
};

export function livenessMeta(status: string | null | undefined): Meta | null {
  if (!status) return null;
  return META[status] ?? { label: status, badge: 'text-slate-600 bg-slate-50 border-slate-200', dot: 'bg-slate-400' };
}

export function LivenessDot({ status, title }: { status: string | null | undefined; title?: string }) {
  const meta = livenessMeta(status);
  if (!meta) return <span className="inline-block h-2 w-2 rounded-full bg-slate-200" title="Liveness not checked" />;
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${meta.dot}`}
      title={title ?? `Liveness: ${meta.label}`}
    />
  );
}

export function LivenessBadge({ status }: { status: string | null | undefined }) {
  const meta = livenessMeta(status);
  if (!meta) {
    return <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-md border text-slate-400 bg-slate-50 border-slate-200">Not checked</span>;
  }
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-md border ${meta.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}
